import {BaseControl} from './base-control';
import {SelectControl} from './select-control';
import {AmountControl} from './amount-control';
import {CheckboxControl} from './checkbox-control';
import {PasswordControl} from './password-control';
import {DatepickerControl} from './datepicker-control';
import {RadioGroupControl} from './radio-group-control';
import {AutocompleteControl} from './autocomplete-control';
import {AddNewSelectControl} from './add-new-select-control';
import {PhoneAutocompleteControl} from './phone-autocomplete-control';
import {CountryAutocompleteControl} from './country-autocomplete-control';

export type CollectionItemType = AddNewSelectControl | AmountControl | AutocompleteControl |
    BaseControl | CheckboxControl | CountryAutocompleteControl | DatepickerControl |
    PasswordControl | SelectControl | PhoneAutocompleteControl | RadioGroupControl;

export class ControlsCollection {
    private readonly items: CollectionItemType[];

    constructor(items: CollectionItemType[]) {
        this.items = items;
    }

    addItem(item: CollectionItemType): void {
        const filteredItems = this.items
            .filter((collectionItem: CollectionItemType) => collectionItem.name === item.name);

        if (filteredItems.length === 0) {
            this.items.push(item);
        }
    }

    getItem(name: string): CollectionItemType {
        const filteredItems = this.items.filter((item: CollectionItemType) => item.name === name);

        if (filteredItems.length > 0) {
            return filteredItems[0];
        }

        return null;
    }

    getItemByIndex(index: number): CollectionItemType {
        if (typeof this.items[index] !== 'undefined') {
            return this.items[index];
        }

        return null;
    }

    removeItem(index: number): void {
        if (typeof this.items[index] !== 'undefined') {
            this.items.splice(index, 1);
        }
    }

    getItems(): CollectionItemType[] {
        return this.items;
    }

    eachItems(cb: (control: CollectionItemType) => void): void {
        this.items.forEach(((item: CollectionItemType) => cb(item)));
    }

    mapItems(cb: (control: CollectionItemType) => CollectionItemType): CollectionItemType[] {
        return this.items.map(((item: CollectionItemType) => cb(item)));
    }

    sortItems(desc = false): CollectionItemType[] {
        return this.items.sort((current: CollectionItemType, next: CollectionItemType) => {
            if (!desc) {
                return current.sortIndex - next.sortIndex;
            }

            return next.sortIndex - current.sortIndex;
        });
    }

    getItemAsAddNewSelectControl(index: number): AddNewSelectControl {
        const item = this.getItemByIndex(index);

        if (item instanceof AddNewSelectControl) {
            return item;
        }

        return null;
    }

    getItemAsAmountControl(index: number): AmountControl {
        const item = this.getItemByIndex(index);

        if (item instanceof AmountControl) {
            return item;
        }

        return null;
    }

    getItemAsAutocompleteControl(index: number): AutocompleteControl {
        const item = this.getItemByIndex(index);

        if (item instanceof AutocompleteControl) {
            return item;
        }

        return null;
    }

    getItemAsBaseControl(index: number): BaseControl {
        const item = this.getItemByIndex(index);

        if (item instanceof BaseControl) {
            return item as BaseControl;
        }

        return null;
    }

    getItemAsCheckboxControl(index: number): CheckboxControl {
        const item = this.getItemByIndex(index);

        if (item instanceof CheckboxControl) {
            return item;
        }

        return null;
    }

    getItemAsCountryAutocompleteControl(index: number): CountryAutocompleteControl {
        const item = this.getItemByIndex(index);

        if (item instanceof CountryAutocompleteControl) {
            return item;
        }

        return null;
    }

    getItemAsDatepickerControl(index: number): DatepickerControl {
        const item = this.getItemByIndex(index);

        if (item instanceof DatepickerControl) {
            return item;
        }

        return null;
    }

    getItemAsPasswordControl(index: number): PasswordControl {
        const item = this.getItemByIndex(index);

        if (item instanceof PasswordControl) {
            return item;
        }

        return null;
    }

    getItemAsSelectControl(index: number): SelectControl {
        const item = this.getItemByIndex(index);

        if (item instanceof SelectControl) {
            return item;
        }

        return null;
    }

    getItemAsPhoneAutocompleteControl(index: number): PhoneAutocompleteControl {
        const item = this.getItemByIndex(index);

        if (item instanceof PhoneAutocompleteControl) {
            return item;
        }

        return null;
    }

    getItemAsRadioGroupControl(index: number): RadioGroupControl {
        const item = this.getItemByIndex(index);

        if (item instanceof RadioGroupControl) {
            return item;
        }

        return null;
    }
}
